<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

const llms = [
  { name: 'OpenAI', icon: '\u2b21' },
  { name: 'Llama 3', icon: '\ud83e\udd99' },
  { name: 'Claude', icon: '\u25c6' },
  { name: 'Mistral', icon: '\u25b2' },
  { name: 'Gemini', icon: '\u2726' },
  { name: 'Ollama', icon: '\u25cb' },
  { name: 'Groq', icon: '\u25c8' },
  { name: 'Azure OpenAI', icon: '\u2b22' },
];

const headingVisible = ref(false);
const visibleChips = ref<boolean[]>(llms.map(() => false));

const headingRef = ref<HTMLElement | null>(null);
const chipRefs = ref<(HTMLElement | null)[]>([]);

let observer: IntersectionObserver | undefined;

const setChipRef = (index: number, element: Element | null) => {
  chipRefs.value[index] = element as HTMLElement | null;
};

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        if (entry.target === headingRef.value) {
          headingVisible.value = true;
        }

        const chipIndex = Number((entry.target as HTMLElement).dataset.index);
        if (!Number.isNaN(chipIndex)) {
          visibleChips.value[chipIndex] = true;
        }

        observer?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.12,
    },
  );

  if (headingRef.value) {
    observer.observe(headingRef.value);
  }

  chipRefs.value.forEach((chip) => {
    if (chip) {
      observer?.observe(chip);
    }
  });
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section class="ai-trust">
    <div class="ai-trust__inner">
      <p ref="headingRef" class="ai-trust__eyebrow" :class="{ 'is-visible': headingVisible }">
        Compatible with every major LLM provider
      </p>
      <div class="ai-trust__chips">
        <div
          v-for="(llm, idx) in llms"
          :key="llm.name"
          class="ai-trust__chip"
          :class="{ 'is-visible': visibleChips[idx] }"
          :ref="(element) => setChipRef(idx, element as Element)"
          :style="{ '--delay': `${idx * 55}ms` }"
          :data-index="idx"
        >
          <span class="ai-trust__icon">{{ llm.icon }}</span>
          <span class="ai-trust__label">{{ llm.name }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.ai-trust {
  background: #1e1530;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.ai-trust__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.ai-trust__eyebrow {
  margin: 0;
  text-align: center;
  color: #4b5563;
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  opacity: 0;
}

.ai-trust__eyebrow.is-visible {
  animation: fadeIn 0.35s ease forwards;
}

.ai-trust__chips {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}

.ai-trust__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  padding: 0.625rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  opacity: 0;
  transform: translateY(10px);
  opacity: 0;
  transition:
    border-color 300ms ease,
    background-color 300ms ease,
    transform 300ms ease;
}

.ai-trust__chip.is-visible {
  animation: fadeLift 0.4s ease var(--delay, 0ms) forwards;
}

.ai-trust__chip:hover {
  border-color: rgba(0, 191, 116, 0.35);
  background: rgba(0, 191, 116, 0.08);
  transform: translateY(-1px);
}

.ai-trust__icon {
  color: #00bf74;
  font-size: 0.875rem;
}

.ai-trust__label {
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 500;
}

@media (max-width: 800px) {
  .ai-trust__inner {
    padding: 3rem 1.5rem;
  }
}

@keyframes fadeLift {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
